import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import { policyTypes } from '@ourbranch/policy-types';

import { Card } from 'core/components/card';
import useSession from 'core/hooks/use-session';
import useBrandFeatures from 'core/hooks/use-brand-features';
import { useStore } from 'core/store';
import { FormField } from 'core/components/form';
import Section from 'core/components/section';
import { AgentDetails } from 'customer/components/agent-details';
import { agentDetailsSourceFromOffer } from 'customer/components/agent-details/agent-details-source';
import useStyles from './settings.styles';

const OfferSource = observer(({ leadSourceOptions }) => {
  const classes = useStyles();
  const { canModifyAffinityAndLeadSource, canToggleEmployeeDiscount } = useSession();
  const { offer: store } = useStore();
  // The Lead Source / Affinity Code fields beside the card are form-controlled, so read the live form
  // values (they mirror quote.*) over the saved quote — the card then tracks edits before save
  // instead of lagging until the store refreshes. (#35737 devlo)
  const formValues = useFormikContext()?.values;
  const agentDetailsSource = agentDetailsSourceFromOffer(
    formValues ? { quote: { ...store.offer?.quote, ...formValues } } : store.offer
  );
  const { autoDiscounts: brandAutoDiscounts } = useBrandFeatures();
  // #35626: a brand that declares its own auto discounts (CFM) has no employee discount on auto.
  const showEmployeeDiscount =
    canToggleEmployeeDiscount && !(brandAutoDiscounts && store.selectedOption === policyTypes.Auto);

  return (
    <Section title="Offer Source" type="SubSection">
      <Card className={classes.overflowContainer} type="secondary">
        <Grid container item xs={12}>
          <Grid container item xs={12}>
            {showEmployeeDiscount && (
              <Grid container className={canModifyAffinityAndLeadSource ? classes.withBottomLine : null}>
                <FormField label="Employee Discount" xs={12} type="switch" name="global.employeeDiscount" mode="dark" />
              </Grid>
            )}

            <Grid container spacing={2}>
              {canModifyAffinityAndLeadSource && (
                <FormField
                  label="Lead Source"
                  xs={8}
                  type="select"
                  name="leadSource"
                  options={leadSourceOptions}
                  mode="dark"
                />
              )}
              <FormField
                label="Affinity Code"
                xs={!canModifyAffinityAndLeadSource ? 8 : 4}
                type="affinity"
                name="global.affinity"
                mode="dark"
                fast={false}
                disabled={!canModifyAffinityAndLeadSource}
              />
            </Grid>
          </Grid>
        </Grid>
      </Card>
      {/* #35737: the single "Rep" value is replaced by the Customer page's Agent Details card — agent,
          agency contact info, Serviced By, lead source and affinity — read off the offer itself
          (quote.rep / quote.global.affinity / quote.leadSource), collapsed by default. */}
      <AgentDetails source={agentDetailsSource} collapsible sectionType="SubSection" />
    </Section>
  );
});

OfferSource.propTypes = {
  leadSourceOptions: PropTypes.array.isRequired
};

export default OfferSource;
